import {FC, useState} from "react";
import {App, Button} from "antd";
import {ThunderboltOutlined} from "@ant-design/icons";
import {useDispatch, useSelector} from "react-redux";
import AxiosServices from "../Services/axiosServices";
import {HasMatchStarted, IMatch} from "../Interfaces/IMatch";
import {IUserBets} from "../Interfaces/IUserBets";
import {AppDispatch, RootState} from "../store";
import {useAuth} from "../config/AuthContext";
import {useGlobal} from "../config/GlobalContext";
import {fetchUserBets} from "./betsSlice";

// Poisson(lambda=1.3) sampler clamped to [0, 20]. Same tuning as the
// global RandomBetButton so a per-match shot and the bulk fill draw
// from the same distribution.
const LAMBDA = 1.3;
function randomScore(): number {
    const L = Math.exp(-LAMBDA);
    let k = 0;
    let p = 1;
    do {
        k++;
        p *= Math.random();
    } while (p > L);
    return Math.min(20, k - 1);
}

type Props = { match: IMatch };

// Square icon-only button that injects a random score on each of the
// match's two matchteams that don't already carry a bet. Gated by
// the user's `randomBet` opt-in and by the match not having kicked
// off yet — once the game starts we never let any bet land.
const MatchRandomBetButton: FC<Props> = ({match}) => {
    const {authUser, isLoggedIn} = useAuth();
    const {currentCmpEdt} = useGlobal();
    const dispatch = useDispatch<AppDispatch>();
    const allBets = useSelector((state: RootState) => state.bets.userBets);
    const [busy, setBusy] = useState(false);
    const {message} = App.useApp();

    if (!isLoggedIn || !authUser?.randomBet) return null;
    if (!match.MatchTeam_Idt_Dom || !match.MatchTeam_Idt_Ext) return null;
    if (HasMatchStarted(match)) return null;

    // Once both sides of the match carry a bet (random OR manual), a
    // re-click is a no-op (the action only fills empty sides), so hide
    // the button outright instead of letting the user tap a dead
    // control.
    const hasDom = allBets.some((b: IUserBets) => b.matchteam_idt === match.MatchTeam_Idt_Dom);
    const hasExt = allBets.some((b: IUserBets) => b.matchteam_idt === match.MatchTeam_Idt_Ext);
    if (hasDom && hasExt) return null;

    const handleClick = async () => {
        if (busy || !authUser?.username || !currentCmpEdt) return;
        setBusy(true);
        try {
            const tasks: Promise<unknown>[] = [];
            const hasDom = allBets.some((b: IUserBets) => b.matchteam_idt === match.MatchTeam_Idt_Dom);
            const hasExt = allBets.some((b: IUserBets) => b.matchteam_idt === match.MatchTeam_Idt_Ext);
            if (!hasDom) tasks.push(AxiosServices.SaveUserBet(match.MatchTeam_Idt_Dom, randomScore(), true));
            if (!hasExt) tasks.push(AxiosServices.SaveUserBet(match.MatchTeam_Idt_Ext, randomScore(), true));
            if (tasks.length === 0) {
                message.info(`Match n° ${match.Num} : déjà parié.`);
                return;
            }
            await Promise.all(tasks);
            await dispatch(fetchUserBets({competition: currentCmpEdt, usr: authUser.username})).unwrap();
            message.success(`Match n° ${match.Num} : ${tasks.length} score(s) aléatoire(s) enregistré(s).`);
        } catch (e: any) {
            message.error(`Échec : ${e?.message ?? 'inconnue'}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Button
            size="small"
            type="primary"
            icon={<ThunderboltOutlined/>}
            loading={busy}
            onClick={handleClick}
            title={`Pari aléatoire sur le match n° ${match.Num}`}
        />
    );
};

export default MatchRandomBetButton;
